import type { ResumeSectionType } from '@applicator/shared'

import { allBlocksTerminal, countFailedBlocks, hasBlocksInFlight } from './blockProgress'
import { resolveBlockDurationMs } from './blockDuration'
import { MICRO_SECTION_BLOCK_ID } from './blockIds'
import type { OptimizationBlockState, ResumeOptimizationRun } from './types'

export type OptimizationAgentPhase =
  | 'idle'
  | 'starting'
  | 'preparing'
  | 'blocks'
  | 'finalizing'
  | 'complete'
  | 'failed'

export type OptimizationAgentHeaderStatus = 'idle' | 'working' | 'success' | 'error'

export type AgentChatMessageKind = 'info' | 'active' | 'done' | 'waiting' | 'error'

/** True when every checklist row is terminal (done or needs-review), none still waiting/active. */
export function areAgentStepsTerminal(messages: AgentChatMessage[]): boolean {
  return (
    messages.length > 0 &&
    messages.every((m) => m.kind === 'done' || m.kind === 'error')
  )
}

export interface AgentChatMessage {
  id: string
  kind: AgentChatMessageKind
  text: string
}

export interface OptimizationAgentUiState {
  showPanel: boolean
  phase: OptimizationAgentPhase
  headerStatus: OptimizationAgentHeaderStatus
  headline: string
  subline: string
  messages: AgentChatMessage[]
  focusSectionTypes: ResumeSectionType[]
  activeBlock: OptimizationBlockState | null
  /** Next pending block while worker is between sections (no status=running yet). */
  preparingBlock: OptimizationBlockState | null
  dimToolbar: boolean
  dimPreview: boolean
  dimAllSections: boolean
}

const PREP_STEP_MS = 3500
const PREP_LONG_WAIT_MS = 12_000

const PREP_STEPS_WITH_WX = [
  'Reading your resume and the job description…',
  'Planning which sections to tailor…',
  'Matching your experience to what this role needs…',
  'Finding the most relevant work history for this job…',
] as const

const PREP_STEPS_NO_WX = [
  'Reading your resume and the job description…',
  'Planning which sections to tailor…',
  'Analyzing what this role is looking for…',
  'Preparing your resume for this role…',
] as const

function blocksStarted(blocks: OptimizationBlockState[]): boolean {
  return blocks.some((b) => b.status !== 'pending')
}

function findRunningBlock(blocks: OptimizationBlockState[]): OptimizationBlockState | null {
  return blocks.find((b) => b.status === 'running') ?? null
}

export function findNextPendingBlock(blocks: OptimizationBlockState[]): OptimizationBlockState | null {
  return blocks.find((b) => b.status === 'pending') ?? null
}

/**
 * Block the agent panel and editor should highlight as active.
 * During worker handoff (previous block done, next not yet `running` in DB),
 * treat the next pending block as in progress so the UI never shows a dead zone.
 */
export function resolveDisplayActiveBlock(blocks: OptimizationBlockState[]): OptimizationBlockState | null {
  const running = findRunningBlock(blocks)
  if (running) return running
  if (!blocksStarted(blocks)) return null
  if (allBlocksTerminal(blocks)) return null
  const next = findNextPendingBlock(blocks)
  if (!next) return null
  return { ...next, status: 'running' }
}

function buildBlockMessages(
  blocks: OptimizationBlockState[],
  optimisticActiveBlockId: string | null
): AgentChatMessage[] {
  return blocks.map((block) => {
    if (
      optimisticActiveBlockId != null &&
      block.block_id === optimisticActiveBlockId &&
      block.status === 'pending'
    ) {
      return { id: block.block_id, kind: 'active', text: `Optimizing ${block.label}…` }
    }
    return blockStatusMessage(block)
  })
}

export function sectionTypesForOptimizationBlock(
  block: OptimizationBlockState
): ResumeSectionType[] {
  if (block.block_id === MICRO_SECTION_BLOCK_ID) {
    return ['certifications', 'interests', 'awards_scholarships']
  }
  if (block.section_type) {
    return [block.section_type]
  }
  return []
}

function formatDuration(ms: number | undefined): string {
  if (ms == null || ms <= 0) return ''
  const seconds = ms / 1000
  return seconds < 10 ? `${seconds.toFixed(1)}s` : `${Math.round(seconds)}s`
}

function blockStatusMessage(block: OptimizationBlockState): AgentChatMessage {
  const duration = formatDuration(resolveBlockDurationMs(block))
  switch (block.status) {
    case 'running':
      return { id: block.block_id, kind: 'active', text: `Optimizing ${block.label}…` }
    case 'done':
      return {
        id: block.block_id,
        kind: 'done',
        text: `${block.label} — done${duration ? ` (${duration})` : ''}`,
      }
    case 'skipped':
      return { id: block.block_id, kind: 'done', text: `${block.label} — skipped` }
    case 'failed':
      return { id: block.block_id, kind: 'error', text: `${block.label} — needs review` }
    default:
      return { id: block.block_id, kind: 'waiting', text: `${block.label} — waiting` }
  }
}

function prepSteps(hasWorkExperience: boolean): readonly string[] {
  return hasWorkExperience ? PREP_STEPS_WITH_WX : PREP_STEPS_NO_WX
}

function prepStepIndex(elapsedMs: number, stepCount: number): number {
  return Math.min(stepCount - 1, Math.floor(elapsedMs / PREP_STEP_MS))
}

function prepSubline(elapsedMs: number, hasWorkExperience: boolean): string {
  if (elapsedMs >= PREP_LONG_WAIT_MS) {
    return hasWorkExperience
      ? 'Still preparing… this step usually takes 15–30 seconds.'
      : 'Still preparing… analyzing the job and your profile usually takes 15–30 seconds.'
  }
  return hasWorkExperience
    ? 'Setting up the tailoring agent.'
    : 'No work history on this resume — we will tailor the other sections.'
}

function idleState(): OptimizationAgentUiState {
  return {
    showPanel: false,
    phase: 'idle',
    headerStatus: 'idle',
    headline: '',
    subline: '',
    messages: [],
    focusSectionTypes: [],
    activeBlock: null,
    preparingBlock: null,
    dimToolbar: false,
    dimPreview: false,
    dimAllSections: false,
  }
}

import type { QualityIssue } from './qualityEvalFlagging'
import { qualityIssueSectionTypes } from './qualityEvalFlagging'
import { sanitizeOptimizationUserMessage } from './optimizationUserMessages'

export function buildOptimizationAgentUiState(params: {
  starting: boolean
  activeRun: ResumeOptimizationRun | null
  runUiStartedAtMs: number | null
  nowMs?: number
  /** When false, prep copy avoids "finding work history" (no bullets to index). */
  hasWorkExperienceBullets?: boolean
  qualityIssues?: QualityIssue[]
}): OptimizationAgentUiState {
  const {
    starting,
    activeRun,
    runUiStartedAtMs,
    hasWorkExperienceBullets = true,
    qualityIssues = [],
  } = params
  const nowMs = params.nowMs ?? Date.now()
  const idle = idleState()

  if (starting) {
    return {
      ...idle,
      showPanel: true,
      phase: 'starting',
      headerStatus: 'working',
      headline: 'Starting optimization…',
      subline: 'Creating your tailoring run.',
      messages: [
        { id: 'start', kind: 'active', text: 'Creating optimization run…' },
        { id: 'billing', kind: 'waiting', text: 'Checking quota and saving job description…' },
      ],
      dimToolbar: true,
      dimPreview: true,
      dimAllSections: true,
    }
  }

  if (!activeRun) return idle

  const blocks = activeRun.blocks ?? []
  const baseMessages: AgentChatMessage[] = [
    { id: 'run-created', kind: 'done', text: 'Optimization run created.' },
  ]

  if (activeRun.status === 'completed') {
    const failedCount = countFailedBlocks(blocks)
    const blockMessages = buildBlockMessages(blocks, null)
    const qualityCount = qualityIssues.length
    return {
      showPanel: true,
      phase: 'complete',
      headerStatus: qualityCount > 0 ? 'error' : 'success',
      headline: qualityCount > 0 ? 'Tailoring finished — review needed' : 'Optimization complete',
      subline:
        qualityCount > 0
          ? `${qualityCount} automated check${qualityCount === 1 ? '' : 's'} need your attention — see Tailoring results.`
          : failedCount > 0
            ? `${failedCount} section${failedCount === 1 ? '' : 's'} need review — open the flagged blocks below.`
            : 'Your resume is tailored for this job. Review the updates in the editor.',
      messages: [
        ...baseMessages,
        ...blockMessages,
        { id: 'complete', kind: 'done', text: 'All sections saved to your resume.' },
      ],
      focusSectionTypes: qualityCount > 0 ? qualityIssueSectionTypes(qualityIssues) : [],
      activeBlock: null,
      preparingBlock: null,
      dimToolbar: false,
      dimPreview: false,
      dimAllSections: false,
    }
  }

  if (activeRun.status === 'failed') {
    const failedCount = countFailedBlocks(blocks)
    const blockMessages = buildBlockMessages(blocks, null)
    const qualityCount = qualityIssues.length
    return {
      showPanel: true,
      phase: 'failed',
      headerStatus: 'error',
      headline:
        failedCount > 0
          ? 'Optimization could not finish'
          : qualityCount > 0
            ? 'Tailoring finished — automated checks failed'
            : 'Optimization could not finish',
      subline:
        failedCount > 0
          ? sanitizeOptimizationUserMessage(
              activeRun.error_message,
              'Some sections need manual review before you can rely on this draft.'
            )
          : qualityCount > 0
            ? `${qualityCount} check${qualityCount === 1 ? '' : 's'} failed (e.g. empty education or contact gaps) — open highlighted sections and Tailoring results.`
            : sanitizeOptimizationUserMessage(
                activeRun.error_message,
                'Try again or pick a different model stack.'
              ),
      messages: [
        ...baseMessages,
        ...blockMessages,
        {
          id: 'failed',
          kind: 'error',
          text:
            failedCount > 0
              ? sanitizeOptimizationUserMessage(
                  activeRun.error_message,
                  'Run ended before we could finish checking your resume.'
                )
              : qualityCount > 0
                ? 'All sections ran, but quality checks flagged items for your review.'
                : sanitizeOptimizationUserMessage(
                    activeRun.error_message,
                    'Run ended before we could finish checking your resume.'
                  ),
        },
      ],
      focusSectionTypes:
        qualityCount > 0 ? qualityIssueSectionTypes(qualityIssues) : [],
      activeBlock: null,
      preparingBlock: null,
      dimToolbar: false,
      dimPreview: false,
      dimAllSections: false,
    }
  }

  const runActive = activeRun.status === 'pending' || activeRun.status === 'running'
  if (!runActive) return idle

  const started = blocksStarted(blocks)
  const displayActiveBlock = resolveDisplayActiveBlock(blocks)
  const optimisticActiveBlockId =
    displayActiveBlock != null &&
    blocks.find((b) => b.block_id === displayActiveBlock.block_id)?.status === 'pending'
      ? displayActiveBlock.block_id
      : null
  const blockMessages = buildBlockMessages(blocks, optimisticActiveBlockId)
  const elapsed = runUiStartedAtMs != null ? Math.max(0, nowMs - runUiStartedAtMs) : 0

  if (!started) {
    const steps = prepSteps(hasWorkExperienceBullets)
    const stepIdx = prepStepIndex(elapsed, steps.length)
    const prepMessages: AgentChatMessage[] = steps.map((text, index) => ({
      id: `prep-${index}`,
      kind: index < stepIdx ? 'done' : index === stepIdx ? 'active' : 'waiting',
      text,
    }))
    return {
      showPanel: true,
      phase: 'preparing',
      headerStatus: 'working',
      headline: steps[stepIdx],
      subline: prepSubline(elapsed, hasWorkExperienceBullets),
      messages: [...baseMessages, ...prepMessages],
      focusSectionTypes: [],
      activeBlock: null,
      preparingBlock: null,
      dimToolbar: true,
      dimPreview: true,
      dimAllSections: true,
    }
  }

  const rawNextPending = findNextPendingBlock(blocks)
  const focusSectionTypes = displayActiveBlock
    ? sectionTypesForOptimizationBlock(displayActiveBlock)
    : []

  if (allBlocksTerminal(blocks) && !hasBlocksInFlight(blocks)) {
    return {
      showPanel: true,
      phase: 'finalizing',
      headerStatus: 'working',
      headline: 'Wrapping up…',
      subline:
        'Saving your tailored resume. Checks already ran as each section finished — this step is save and final review.',
      messages: [
        ...baseMessages,
        ...blockMessages,
        {
          id: 'finalizing',
          kind: 'active',
          text: 'Saving your resume and finishing final review…',
        },
      ],
      focusSectionTypes: [],
      activeBlock: null,
      preparingBlock: null,
      dimToolbar: false,
      dimPreview: false,
      dimAllSections: false,
    }
  }

  const phase: OptimizationAgentPhase = 'blocks'

  let headline = 'Applying optimizations…'
  let subline = 'Tailoring one section at a time.'
  if (displayActiveBlock) {
    headline = `Optimizing ${displayActiveBlock.label}…`
    subline = 'Other sections are dimmed until this step finishes.'
  }

  return {
    showPanel: true,
    phase,
    headerStatus: 'working',
    headline,
    subline,
    messages: [...baseMessages, ...blockMessages],
    focusSectionTypes,
    activeBlock: displayActiveBlock,
    preparingBlock: optimisticActiveBlockId ? rawNextPending : null,
    dimToolbar: true,
    dimPreview: true,
    dimAllSections: false,
  }
}

export function isSectionDimmedForOptimization(
  sectionType: ResumeSectionType,
  agentUi: OptimizationAgentUiState
): boolean {
  if (!agentUi.showPanel) return false
  if (agentUi.dimAllSections) return true
  if (agentUi.phase === 'finalizing' || agentUi.phase === 'complete' || agentUi.phase === 'failed') {
    return false
  }
  if (agentUi.phase !== 'blocks') return false
  if (!agentUi.focusSectionTypes.length) return false
  return !agentUi.focusSectionTypes.includes(sectionType)
}

export function isSectionFocusedForOptimization(
  sectionType: ResumeSectionType,
  agentUi: OptimizationAgentUiState
): boolean {
  if (!agentUi.showPanel) return false
  if (agentUi.dimAllSections) return false
  return agentUi.focusSectionTypes.includes(sectionType)
}

export function isSectionQualityFlagged(
  sectionType: ResumeSectionType,
  flaggedSectionTypes: ResumeSectionType[]
): boolean {
  return flaggedSectionTypes.includes(sectionType)
}
